import { useState, useCallback, useMemo } from 'react';
import { archetypes } from '../data/archetypes';
import styles from './PlanetLandscape.module.css';

// API 地址
const getApiUrl = () => {
  if (process.env.REACT_APP_ORACLE_API) {
    return process.env.REACT_APP_ORACLE_API;
  }
  if (process.env.NODE_ENV === 'production') {
    return '';
  }
  const host = window.location.hostname;
  if (host !== 'localhost' && host !== '127.0.0.1') {
    return `http://${host}:3001`;
  }
  return 'http://localhost:3001';
};
const API_URL = getApiUrl();

export default function PlanetLandscape({ scores, onSave, onClose, onImageGenerated }) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [error, setError] = useState(null);
  const [prompt, setPrompt] = useState(null);

  // 获取排序后的原型
  const getSortedArchetypes = useCallback(() => {
    return Object.entries(scores)
      .sort((a, b) => b[1] - a[1])
      .map(([key, score]) => ({ key, score, ...archetypes[key] }));
  }, [scores]);

  const sorted = getSortedArchetypes();
  const primary = sorted[0];
  const secondary = sorted[1];

  // 生成随机星星
  const stars = useMemo(() => 
    Array.from({ length: 50 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 2 + 1,
      delay: Math.random() * 3,
      duration: 2 + Math.random() * 2,
    })), []);

  // 生成流星
  const meteors = useMemo(() => 
    Array.from({ length: 3 }).map((_, i) => ({
      id: i,
      delay: i * 4 + Math.random() * 2,
    })), []);

  // 调用即梦 API 生成图像
  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    setPrompt(null);

    try {
      const response = await fetch(`${API_URL}/api/generate-landscape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scores }),
      });

      const data = await response.json();

      if (data.success && data.imageUrl) {
        setImageUrl(data.imageUrl);
        setPrompt(data.prompt);
        // 通知父组件图片已生成
        onImageGenerated?.(data.imageUrl);
      } else {
        setError(data.error || '生成失败');
        setPrompt(data.prompt); // 即使失败也返回 prompt
      }
    } catch (err) {
      setError('网络错误，请检查服务器是否运行');
      console.error('Generate error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  // 保存图片
  const handleSave = async () => {
    if (!imageUrl) return;

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.download = `${primary?.name}-星球风景-${Date.now()}.png`;
      link.href = url;
      link.click();
      
      URL.revokeObjectURL(url);
      onSave?.();
    } catch (err) {
      // 如果跨域无法下载，打开新窗口
      window.open(imageUrl, '_blank');
    }
  };

  // 复制 prompt
  const handleCopyPrompt = () => {
    if (prompt) {
      navigator.clipboard.writeText(prompt);
      alert('Prompt 已复制到剪贴板！');
    }
  };

  return (
    <div className={styles.overlay}>
      {/* 背景星空 */}
      <div className={styles.starField}>
        {stars.map(star => (
          <div
            key={star.id}
            className={styles.star}
            style={{
              left: `${star.left}%`,
              top: `${star.top}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
            }}
          />
        ))}
        {meteors.map(meteor => (
          <div
            key={meteor.id}
            className={styles.meteor}
            style={{ animationDelay: `${meteor.delay}s` }}
          />
        ))}
      </div>

      {/* 背景光晕 */}
      <div className={styles.bgGlow} />
      <div className={styles.bgGlow2} />

      <div className={styles.modal}>
        {/* 角落装饰 */}
        <div className={styles.cornerTL} />
        <div className={styles.cornerTR} />
        <div className={styles.cornerBL} />
        <div className={styles.cornerBR} />

        {/* 顶部装饰线 */}
        <div className={styles.topDecor}>
          <span className={styles.decorStar}>✧</span>
          <div className={styles.decorLine} />
          <span className={styles.decorDiamond}>◇</span>
          <div className={styles.decorLine} />
          <span className={styles.decorStar}>✧</span>
        </div>

        <button className={styles.closeBtn} onClick={onClose}>×</button>

        <h2 className={styles.title}>你的专属星球风景</h2>
        <p className={styles.subtitle}>
          <span className={styles.subtitleIcon}>✦</span>
          {primary?.name} · {primary?.planet}
          {secondary && ` × ${secondary?.name}`}
          <span className={styles.subtitleIcon}>✦</span>
        </p>

        <div className={styles.canvasWrapper}>
          {/* 画布边框装饰 */}
          <div className={styles.canvasBorder} />
          
          {/* 扫描线效果 */}
          <div className={styles.scanLine} />

          {!imageUrl && !isGenerating && !error && (
            <div className={styles.placeholder}>
              {/* 星座连线装饰 */}
              <svg className={styles.constellation} viewBox="0 0 200 150">
                <circle cx="30" cy="30" r="2" fill="rgba(180,160,220,0.8)" />
                <circle cx="80" cy="50" r="3" fill="rgba(180,160,220,0.9)" />
                <circle cx="120" cy="25" r="2" fill="rgba(180,160,220,0.7)" />
                <circle cx="170" cy="60" r="2.5" fill="rgba(180,160,220,0.8)" />
                <circle cx="100" cy="100" r="3" fill="rgba(180,160,220,0.9)" />
                <circle cx="150" cy="120" r="2" fill="rgba(180,160,220,0.7)" />
                <line x1="30" y1="30" x2="80" y2="50" stroke="rgba(180,160,220,0.3)" strokeWidth="1" />
                <line x1="80" y1="50" x2="120" y2="25" stroke="rgba(180,160,220,0.3)" strokeWidth="1" />
                <line x1="120" y1="25" x2="170" y2="60" stroke="rgba(180,160,220,0.3)" strokeWidth="1" />
                <line x1="80" y1="50" x2="100" y2="100" stroke="rgba(180,160,220,0.3)" strokeWidth="1" />
                <line x1="100" y1="100" x2="150" y2="120" stroke="rgba(180,160,220,0.3)" strokeWidth="1" />
              </svg>

              <div className={styles.placeholderIcon}>✧</div>
              <p>点击下方按钮生成你的专属星球风景</p>
              <p className={styles.placeholderHint}>
                基于你的原型组合，AI 将为你绘制独一无二的星球画面
              </p>
              
              {/* 能量指示器 */}
              <div className={styles.energyIndicator}>
                <div className={styles.energyRing}>
                  <div className={styles.energyDot} style={{ background: primary?.color || '#a78bfa' }} />
                </div>
                <span>能量就绪</span>
              </div>
            </div>
          )}

          {isGenerating && (
            <div className={styles.loading}>
              {/* 星尘粒子 */}
              <div className={styles.loadingStardust}>
                {Array.from({ length: 12 }).map((_, i) => (
                  <div
                    key={i}
                    className={styles.loadingParticle}
                    style={{
                      left: `${20 + Math.random() * 60}%`,
                      top: `${30 + Math.random() * 40}%`,
                      animationDelay: `${i * 0.25}s`,
                    }}
                  />
                ))}
              </div>
              
              {/* 轨道系统 */}
              <div className={styles.loadingOrbit}>
                <div className={styles.loadingPlanet} />
                <div className={styles.loadingRing} />
                <div className={styles.loadingRing} />
                <div className={styles.loadingRing} />
                <div className={styles.loadingSatellite} />
                <div className={styles.loadingSatellite} />
                <div className={styles.loadingSatellite} />
              </div>
              
              <p className={styles.loadingText}>AI 正在绘制你的星球...</p>
              <p className={styles.loadingHint}>星际画师正在捕捉你的能量特征</p>
              
              {/* 进度条 */}
              <div className={styles.loadingProgress}>
                <div className={styles.loadingProgressBar} />
              </div>
            </div>
          )}

          {error && (
            <div className={styles.error}>
              <p className={styles.errorText}>{error}</p>
              {prompt && (
                <div className={styles.promptBox}>
                  <p className={styles.promptLabel}>你可以复制以下 Prompt 到即梦或其他 AI 绘图工具：</p>
                  <p className={styles.promptText}>{prompt}</p>
                  <button className={styles.copyBtn} onClick={handleCopyPrompt}>
                    复制 Prompt
                  </button>
                </div>
              )}
            </div>
          )}

          {imageUrl && (
            <img
              src={imageUrl}
              alt={`${primary?.name}的星球风景`}
              className={styles.generatedImage}
            />
          )}
        </div>

        <div className={styles.info}>
          {imageUrl ? (
            <p>这幅风景画由 AI 根据你的原型能量分布生成，独一无二</p>
          ) : (
            <p>融合 {primary?.name} 与 {secondary?.name || '其他原型'} 的能量特质</p>
          )}
        </div>

        <div className={styles.buttons}>
          {!imageUrl ? (
            <button
              className={styles.generateBtn}
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              <span className={styles.btnIcon}>✧</span>
              {isGenerating ? '生成中...' : '生成星球风景'}
              <span className={styles.btnGlow} />
            </button>
          ) : (
            <>
              <button className={styles.saveBtn} onClick={handleSave}>
                <span className={styles.btnIcon}>↓</span>
                保存图片
              </button>
              <button className={styles.regenerateBtn} onClick={handleGenerate}>
                <span className={styles.btnIcon}>↻</span>
                重新生成
              </button>
            </>
          )}
          <button className={styles.closeButton} onClick={onClose}>
            关闭
          </button>
        </div>

        {/* 底部装饰 */}
        <div className={styles.bottomDecor}>
          <div className={styles.decorDot} />
          <div className={styles.decorDot} />
          <div className={styles.decorDot} />
        </div>
      </div>
    </div>
  );
}
